import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Button, Dialog } from "./ui";

interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  /** Label for the primary (confirm) button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the secondary (cancel) button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Style of the primary button. Use "danger" for destructive actions. */
  variant?: "primary" | "danger";
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setOpts(o);
    });
  }, []);

  function settle(value: boolean) {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setOpts(null);
  }

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <Dialog
        open={opts !== null}
        onClose={() => settle(false)}
        title={opts?.title ?? ""}
        primaryAction={
          <Button
            variant={opts?.variant === "danger" ? "danger" : "primary"}
            onClick={() => settle(true)}
            data-autofocus
          >
            {opts?.confirmLabel ?? "Confirm"}
          </Button>
        }
        secondaryAction={
          <Button variant="secondary" onClick={() => settle(false)}>
            {opts?.cancelLabel ?? "Cancel"}
          </Button>
        }
      >
        {opts?.body ?? null}
      </Dialog>
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConfirm must be used inside ConfirmProvider");
  return v;
}
